import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import Input from '../../../components/ui/Input';
import {
  DEFAULT_INGEST_SOURCE,
  INGEST_SOURCE_OPTIONS,
  buildIngestPayload,
} from '../constants';
import FormOptionCard from '../../../components/form/FormOptionCard';
import FormSection from '../../../components/form/FormSection';
import GoogleDriveSourceFields from './GoogleDriveSourceFields';
import S3SourceFields from './S3SourceFields';
import ContentfulSourceFields from './ContentfulSourceFields';
import UrlSourceFields from './UrlSourceFields';
import { SOURCE_TYPE_ICONS } from '../../../support/iconMappings';

const INITIAL_VALUES = {
  name: '',
  url: '',
  s3Url: '',
  awsAccessKeyId: '',
  awsSecretAccessKey: '',
  folderUrl: '',
  folderId: '',
  folderName: '',
  workspaceEmail: '',
  spaceId: '',
  environment: 'master',
  apiUrl: 'https://cdn.contentful.com',
  accessToken: '',
  locale: '*',
  contentTypes: '',
  includeAssets: true,
  includeEntries: true,
};

function KnowledgeIngestForm({
  initialValues = {},
  googleDriveInfo = null,
  onSubmit,
  isSubmitting = false,
  submitLabel = 'Cadastrar base',
  error = '',
}) {
  const { search: driveRefreshKey } = useLocation();
  const [source, setSource] = useState(
    initialValues.source || DEFAULT_INGEST_SOURCE,
  );
  const [values, setValues] = useState({ ...INITIAL_VALUES, ...initialValues });
  const [driveStatus, setDriveStatus] = useState({ connected: false, google_email: null });
  const [workspaceMode, setWorkspaceMode] = useState(false);

  const oauthConfigured = googleDriveInfo?.oauth_configured === true;

  const setField = (field) => (event) => {
    setValues((current) => ({ ...current, [field]: event.target.value }));
  };

  const setCheckbox = (field, checked) => {
    setValues((current) => ({ ...current, [field]: checked }));
  };

  const handleFolderUrlChange = (event) => {
    const folderUrl = event.target.value;
    setValues((current) => ({
      ...current,
      folderUrl,
      folderId: '',
      folderName: '',
    }));
  };

  const handleFolderPickerChange = ({ folderId, folderName, folderUrl }) => {
    setValues((current) => ({
      ...current,
      folderId,
      folderName,
      folderUrl,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit(
      buildIngestPayload(source, values, {
        authMode: workspaceMode ? 'domain_delegation' : 'oauth',
      }),
    );
  };

  const driveConnected = driveStatus.connected;
  const canSubmitDrive =
    source !== 'google_drive' ||
    workspaceMode ||
    driveConnected;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <FormSection
        title="Origem do conteúdo"
        description="Escolha de onde os arquivos serão importados."
      >
        <div
          className={
            INGEST_SOURCE_OPTIONS.length > 3
              ? 'grid grid-cols-1 items-stretch gap-3 sm:grid-cols-2'
              : 'grid grid-cols-1 items-stretch gap-3 sm:grid-cols-3'
          }
        >
          {INGEST_SOURCE_OPTIONS.map((option) => (
            <FormOptionCard
              key={option.value}
              selected={source === option.value}
              title={option.label}
              description={option.description}
              badge={option.recommended ? 'Recomendado' : undefined}
              icon={SOURCE_TYPE_ICONS[option.value]}
              onClick={() => setSource(option.value)}
            />
          ))}
        </div>
      </FormSection>

      <FormSection
        title="Configuração"
        description="Informe os detalhes da origem selecionada."
        bordered
      >
        {source === 'google_drive' && (
          <GoogleDriveSourceFields
            values={values}
            driveRefreshKey={driveRefreshKey}
            googleDriveInfo={googleDriveInfo}
            oauthConfigured={oauthConfigured}
            workspaceMode={workspaceMode}
            onWorkspaceModeChange={setWorkspaceMode}
            driveConnected={driveConnected}
            onDriveStatusChange={setDriveStatus}
            onFieldChange={setField}
            onFolderUrlChange={handleFolderUrlChange}
            onFolderPickerChange={handleFolderPickerChange}
          />
        )}
        {source === 'url' && (
          <UrlSourceFields values={values} onFieldChange={setField} />
        )}
        {source === 's3' && (
          <S3SourceFields values={values} onFieldChange={setField} />
        )}
        {source === 'contentful' && (
          <ContentfulSourceFields
            values={values}
            onFieldChange={setField}
            onCheckboxChange={setCheckbox}
          />
        )}
      </FormSection>

      <FormSection
        title="Identificação"
        description="Nome da base no seu agente."
        bordered
      >
        <Input
          id="kb_name"
          label="Nome da base"
          value={values.name}
          onChange={setField('name')}
          placeholder="FAQ do produto"
          required
        />
      </FormSection>

      {error && (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={isSubmitting || !canSubmitDrive}
        className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSubmitting ? 'Salvando...' : submitLabel}
      </button>
    </form>
  );
}

export default KnowledgeIngestForm;
